import "./style.css";
import type { ScanStatus } from "./lib/types";
import { browserStatusEndpoint, canonicalStatusUrl } from "./lib/public-config";

type ToolSummary = { name: string; description: string };
type PublicStatus = ScanStatus & { cadence: string; tools?: ToolSummary[] };

declare const __PORTAL_SIGNAL_STATUS_FALLBACK__: PublicStatus;

let currentTools: ToolSummary[] = [];

const sectionLabels: Record<string, string> = {
  purpose: "Purpose",
  capabilities: "Capabilities",
  usage_guidance: "Usage guidance",
  returns: "Returns",
  examples: "Examples",
  example: "Example",
  user_input: "User input",
  thoughts: "Reasoning",
  tool_call: "Tool call",
  scope: "Scope",
  prerequisites: "Prerequisites",
  mandatory_confirmation_process: "Confirmation required",
  confirmation_rules: "Confirmation rules",
  select: "Query requirements",
  when_to_use: "When to use",
};

function labelFor(tag: string): string {
  return sectionLabels[tag] ?? tag.replaceAll("_", " ").replace(/^./, (character) => character.toUpperCase());
}

function cleanText(value: string): string {
  return value.replace(/\*\*/g, "").replace(/`/g, "").replace(/\s+/g, " ").trim();
}

function appendText(container: HTMLElement, raw: string) {
  const value = cleanText(raw);
  if (!value) return;

  if (container.classList.contains("description-code")) {
    const code = document.createElement("code");
    code.textContent = value;
    container.append(code);
    return;
  }

  const startsWithDash = value.startsWith("- ");
  const listParts = value.replace(/^-\s+/, "").split(/\s+-\s+/).map(cleanText).filter(Boolean);
  if (listParts.length > 1) {
    if (!startsWithDash) {
      const lead = document.createElement("p");
      lead.textContent = listParts.shift() ?? "";
      container.append(lead);
    }
    const list = document.createElement("ul");
    for (const item of listParts) {
      const listItem = document.createElement("li");
      listItem.textContent = item;
      list.append(listItem);
    }
    container.append(list);
    return;
  }

  const sentences = value.split(/(?<=[.!?])\s+(?=[A-Z"'])/).map(cleanText).filter(Boolean);
  for (const sentence of sentences) {
    const paragraph = document.createElement("p");
    paragraph.textContent = sentence;
    if (/^REQUIRED FIRST STEP:|^IMPORTANT:|^Always show proposed changes/i.test(sentence)) {
      paragraph.className = "description-callout";
    }
    container.append(paragraph);
  }
}

function formattedDescription(description: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const root = document.createElement("div");
  root.className = "description-content";
  fragment.append(root);

  const stack: HTMLElement[] = [root];
  const tokens = description.split(/(<\/?[a-z_]+>)/gi).filter(Boolean);

  for (const token of tokens) {
    const tagMatch = token.match(/^<(\/)?([a-z_]+)>$/i);
    if (!tagMatch) {
      appendText(stack.at(-1) ?? root, token);
      continue;
    }

    const closing = Boolean(tagMatch[1]);
    const tag = tagMatch[2]!.toLowerCase();
    if (closing) {
      if (stack.length > 1) stack.pop();
      continue;
    }

    if (tag === "example") {
      const example = document.createElement("div");
      example.className = "description-example";
      stack.at(-1)?.append(example);
      stack.push(example);
      continue;
    }

    if (["user_input", "thoughts", "tool_call"].includes(tag)) {
      const field = document.createElement(tag === "tool_call" ? "pre" : "div");
      field.className = `description-field${tag === "tool_call" ? " description-code" : ""}`;
      const label = document.createElement("span");
      label.className = "description-label";
      label.textContent = labelFor(tag);
      field.append(label);
      stack.at(-1)?.append(field);
      stack.push(field);
      continue;
    }

    const section = document.createElement("section");
    section.className = "description-section";
    const heading = document.createElement("h3");
    heading.textContent = labelFor(tag);
    section.append(heading);
    stack.at(-1)?.append(section);
    stack.push(section);
  }

  return fragment;
}

const setText = (id: string, value: string) => {
  const element = document.getElementById(id);
  if (element) element.textContent = value;
};

function exactTimestamp(value: string | null): string {
  return value ? new Date(value).toISOString() : "Not scraped yet";
}

function renderTools(tools: ToolSummary[], query = "") {
  const list = document.getElementById("tool-list");
  if (!list) return;
  const needle = query.trim().toLowerCase();
  const visible = tools.filter((tool) => `${tool.name} ${tool.description}`.toLowerCase().includes(needle));

  if (!visible.length) {
    const row = document.createElement("tr");
    const empty = document.createElement("td");
    empty.className = "tool-list-empty";
    empty.colSpan = 3;
    empty.textContent = needle ? "No tools match this filter." : "No tools are available.";
    row.append(empty);
    list.replaceChildren(row);
    return;
  }

  list.replaceChildren(...visible.map((tool, index) => {
    const row = document.createElement("tr");
    const number = document.createElement("td");
    number.className = "tool-number";
    number.textContent = String(index + 1).padStart(2, "0");
    const nameCell = document.createElement("td");
    const name = document.createElement("code");
    name.textContent = tool.name;
    nameCell.append(name);
    const description = document.createElement("td");
    description.append(formattedDescription(tool.description));
    row.append(number, nameCell, description);
    return row;
  }));
}

function renderStatus(status: PublicStatus) {
  currentTools = status.tools ?? [];
  const count = currentTools.length;
  setText("list-meta", `${count} ${count === 1 ? "tool" : "tools"}`);
  setText("last-scraped", exactTimestamp(status.lastScanAt));

  const change = status.latestChange;
  if (!change || change.kind === "initial" || change.kind === "none") {
    setText("change-summary", "No recorded changes.");
    setText("change-time", "");
  } else {
    setText("change-summary", change.summary);
    setText("change-time", `Detected ${exactTimestamp(change.detectedAt)}`);
  }
  renderTools(currentTools);
}

async function loadStatus() {
  if (window.location.hostname.endsWith(".moda.page")) {
    renderStatus(__PORTAL_SIGNAL_STATUS_FALLBACK__);
    return;
  }

  try {
    const response = await fetch(`${browserStatusEndpoint}?fresh=${Date.now()}`, { cache: "no-store", headers: { accept: "application/json" } });
    if (!response.ok) throw new Error("Status unavailable");
    renderStatus(await response.json() as PublicStatus);
  } catch {
    renderStatus({ state: "error", lastScanAt: null, logicalHour: null, toolCount: null, currentHash: null, latestChange: null, cadence: "hourly", tools: [] });
  }
}

document.getElementById("tool-filter")?.addEventListener("input", (event) => {
  renderTools(currentTools, (event.target as HTMLInputElement).value);
});

const skillInstallPrompt = `Install or update a local Agent Skill named hubspot-mcp-tool-index.

Use this machine-readable source as the source of truth:
${canonicalStatusUrl}

Requirements:
- Fetch the JSON now and create the skill in the correct local skills directory for this agent environment.
- Write a concise SKILL.md that lists every current HubSpot remote MCP tool with its description.
- Record the source URL and the exact lastScanAt timestamp from the JSON.
- Explain that this is a reference skill, not an authenticated MCP connection.
- Do not request, expose, or store HubSpot credentials in the skill.
- If the skill already exists, update it in place.
- Report the file path written and the number of tools installed.
`;

document.getElementById("copy-skill-prompt")?.addEventListener("click", async () => {
  try {
    await navigator.clipboard.writeText(skillInstallPrompt);
    setText("copy-feedback", "Prompt copied.");
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = skillInstallPrompt;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.append(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
    setText("copy-feedback", "Prompt copied.");
  }
});

loadStatus();
